import { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { X, Plus, Trash2, Save, Eye, EyeOff, MousePointer2, Move, Check, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

const EMOJI_OPTIONS = ['🥩', '🍄', '🌿', '🧀', '🍋', '🧅', '🧄', '🫒', '🌶️', '🥬', '🍅', '🥕', '🍳', '🧈', '🍯', '🥛', '✨', '🔥', '💎', '⭐'];

export default function LabelEditor({ 
    imageUrl, 
    videoUrl,
    labels = [], 
    onSave, 
    onClose,
    primaryColor = '#C9A227'
}) {
    const containerRef = useRef(null);
    const [editingLabels, setEditingLabels] = useState(labels);
    const [selectedLabelId, setSelectedLabelId] = useState(null);
    const [showPreview, setShowPreview] = useState(false);
    const [showOnboarding, setShowOnboarding] = useState(labels.length === 0);
    
    // Dragging state
    const [isDragging, setIsDragging] = useState(false);
    const [dragType, setDragType] = useState(null);
    const dragStartPos = useRef({ x: 0, y: 0 });

    const selectedLabel = editingLabels.find(l => l.id === selectedLabelId);

    // Click on image to add new anchor point
    const handleImageClick = (e) => {
        if (isDragging || showPreview) return;
        
        const rect = containerRef.current.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        
        // Create new label with default values
        const newLabel = {
            id: `label-${Date.now()}`,
            title: 'New Ingredient',
            subtitle: '',
            icon: '✨',
            anchor_x_percent: x,
            anchor_y_percent: y,
            card_x_percent: Math.min(85, Math.max(15, x + (x < 50 ? 20 : -20))),
            card_y_percent: Math.max(10, y - 5),
        };
        
        setEditingLabels([...editingLabels, newLabel]);
        setSelectedLabelId(newLabel.id);
        setShowOnboarding(false);
    };

    // Update label
    const updateLabel = (id, updates) => {
        setEditingLabels(prev => prev.map(l => 
            l.id === id ? { ...l, ...updates } : l
        ));
    };

    // Delete label
    const deleteLabel = (id) => {
        setEditingLabels(prev => prev.filter(l => l.id !== id));
        if (selectedLabelId === id) {
            setSelectedLabelId(null);
        }
        toast.success('Label deleted');
    };

    // Drag handlers for card
    const handleCardDragStart = (e, labelId) => {
        e.stopPropagation();
        e.preventDefault();
        
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        
        setIsDragging(true);
        setDragType('card');
        setSelectedLabelId(labelId);
        dragStartPos.current = { x: clientX, y: clientY };
    };

    const handleDrag = useCallback((e) => {
        if (!isDragging || !selectedLabelId || !containerRef.current) return;
        
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        
        const rect = containerRef.current.getBoundingClientRect();
        const x = Math.max(10, Math.min(90, ((clientX - rect.left) / rect.width) * 100));
        const y = Math.max(5, Math.min(95, ((clientY - rect.top) / rect.height) * 100));
        
        if (dragType === 'card') {
            updateLabel(selectedLabelId, { card_x_percent: x, card_y_percent: y });
        } else if (dragType === 'anchor') {
            updateLabel(selectedLabelId, { anchor_x_percent: x, anchor_y_percent: y });
        }
    }, [isDragging, selectedLabelId, dragType]);

    const handleDragEnd = useCallback(() => {
        setIsDragging(false);
        setDragType(null);
    }, []);

    useEffect(() => {
        if (isDragging) {
            const options = { passive: false };
            window.addEventListener('mousemove', handleDrag, options);
            window.addEventListener('mouseup', handleDragEnd);
            window.addEventListener('touchmove', handleDrag, options);
            window.addEventListener('touchend', handleDragEnd);
        }
        return () => {
            window.removeEventListener('mousemove', handleDrag);
            window.removeEventListener('mouseup', handleDragEnd);
            window.removeEventListener('touchmove', handleDrag);
            window.removeEventListener('touchend', handleDragEnd);
        };
    }, [isDragging, handleDrag, handleDragEnd]);

    // Save labels
    const handleSave = async () => {
        try {
            await onSave(editingLabels);
            toast.success('Labels saved successfully!');
        } catch (err) {
            toast.error('Failed to save labels');
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-gradient-to-br from-[#0a0a0a] to-[#1a1a1a] flex flex-col">
            {/* Header */}
            <header className="bg-black/50 backdrop-blur-xl border-b border-white/10 px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={onClose} 
                        className="text-white hover:bg-white/10 rounded-full"
                    >
                        <X className="w-5 h-5" />
                    </Button>
                    <div>
                        <h1 className="text-white font-semibold flex items-center gap-2">
                            <Sparkles className="w-4 h-4" style={{ color: primaryColor }} />
                            Ingredient Label Editor
                        </h1>
                        <p className="text-xs text-white/50">Click anywhere on the image to add labels</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-white/40 text-sm mr-2">{editingLabels.length} labels</span>
                    <Button 
                        variant="ghost" 
                        onClick={() => setShowPreview(!showPreview)}
                        className={`text-white hover:bg-white/10 gap-2 rounded-full ${showPreview ? 'bg-white/10' : ''}`}
                    >
                        {showPreview ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        <span className="hidden sm:inline">{showPreview ? 'Edit Mode' : 'Preview'}</span>
                    </Button>
                    <Button 
                        onClick={handleSave}
                        className="gap-2 rounded-full px-6"
                        style={{ backgroundColor: primaryColor }}
                        data-testid="save-labels-btn"
                    >
                        <Save className="w-4 h-4" /> Save
                    </Button>
                </div>
            </header>

            {/* Main Content */}
            <div className="flex-1 flex overflow-hidden">
                {/* Image Canvas */}
                <div className="flex-1 p-4 sm:p-8 flex items-center justify-center overflow-auto">
                    <div className="relative w-full max-w-3xl">
                        {/* Onboarding Hint */}
                        <AnimatePresence>
                            {showOnboarding && !showPreview && (
                                <motion.div
                                    initial={{ opacity: 0, y: -20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -20 }}
                                    className="absolute -top-16 left-1/2 -translate-x-1/2 z-20"
                                >
                                    <div 
                                        className="px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2 shadow-xl"
                                        style={{ backgroundColor: primaryColor, color: '#fff' }}
                                    >
                                        <MousePointer2 className="w-4 h-4" />
                                        Click on the image to add your first label
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* Image Container */}
                        <div 
                            ref={containerRef}
                            className={`relative rounded-2xl overflow-hidden shadow-2xl ${
                                !showPreview ? 'cursor-crosshair' : ''
                            }`}
                            style={{ 
                                aspectRatio: '4/3',
                                boxShadow: `0 25px 80px -20px ${primaryColor}30`
                            }}
                            onClick={handleImageClick}
                        >
                            {/* Media (Image or Video) */}
                            {videoUrl ? (
                                <video
                                    src={videoUrl}
                                    className="w-full h-full object-cover select-none"
                                    autoPlay muted loop playsInline
                                    draggable={false}
                                />
                            ) : (
                                <img 
                                    src={imageUrl} 
                                    alt="Dish" 
                                    className="w-full h-full object-cover select-none"
                                    draggable={false}
                                />
                            )}
                            
                            {/* Overlay for edit mode hint */}
                            {!showPreview && editingLabels.length === 0 && (
                                <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                                    <div className="text-center text-white">
                                        <MousePointer2 className="w-12 h-12 mx-auto mb-3 opacity-70" />
                                        <p className="text-lg font-medium">Click to add a label</p>
                                        <p className="text-sm opacity-70">Mark ingredients on your dish</p>
                                    </div>
                                </div>
                            )}

                            {/* Labels */}
                            <AnimatePresence>
                                {editingLabels.map((label, index) => (
                                    <LabelOverlay
                                        key={label.id}
                                        label={label}
                                        index={index}
                                        isSelected={selectedLabelId === label.id}
                                        isPreview={showPreview}
                                        primaryColor={primaryColor}
                                        onSelect={() => !showPreview && setSelectedLabelId(label.id)}
                                        onCardDragStart={(e) => handleCardDragStart(e, label.id)}
                                        onAnchorDragStart={(e) => {
                                            e.stopPropagation();
                                            setIsDragging(true);
                                            setDragType('anchor');
                                            setSelectedLabelId(label.id);
                                        }}
                                        onDelete={() => deleteLabel(label.id)}
                                    />
                                ))}
                            </AnimatePresence>
                        </div>

                        {/* Instructions */}
                        {!showPreview && (
                            <div className="mt-4 flex justify-center gap-6 text-white/40 text-xs">
                                <span className="flex items-center gap-1">
                                    <MousePointer2 className="w-3 h-3" /> Click to add
                                </span>
                                <span className="flex items-center gap-1">
                                    <Move className="w-3 h-3" /> Drag card to move
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Side Panel - Label Editor */}
                {!showPreview && (
                    <motion.div 
                        initial={{ x: 100, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        className="w-80 bg-black/30 backdrop-blur-xl border-l border-white/10 flex flex-col"
                    >
                        {/* Panel Header */}
                        <div className="p-4 border-b border-white/10">
                            <h3 className="text-white font-medium">
                                {selectedLabel ? 'Edit Label' : 'Labels'}
                            </h3>
                            <p className="text-white/50 text-xs mt-1">
                                {selectedLabel ? 'Customize your label below' : `${editingLabels.length} label${editingLabels.length !== 1 ? 's' : ''} added`}
                            </p>
                        </div>

                        {/* Label Form or List */}
                        <div className="flex-1 overflow-y-auto p-4">
                            {selectedLabel ? (
                                <LabelForm 
                                    label={selectedLabel}
                                    onUpdate={(updates) => updateLabel(selectedLabel.id, updates)}
                                    onDelete={() => deleteLabel(selectedLabel.id)}
                                    onDone={() => setSelectedLabelId(null)}
                                    primaryColor={primaryColor}
                                />
                            ) : editingLabels.length === 0 ? (
                                <div className="text-center py-12">
                                    <div 
                                        className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center"
                                        style={{ backgroundColor: `${primaryColor}20` }}
                                    >
                                        <Plus className="w-8 h-8" style={{ color: primaryColor }} />
                                    </div>
                                    <p className="text-white/70 mb-2">No labels yet</p>
                                    <p className="text-white/40 text-sm">Click on the image to add ingredient labels</p>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {editingLabels.map((label, index) => (
                                        <motion.div
                                            key={label.id}
                                            initial={{ opacity: 0, x: 20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: index * 0.05 }}
                                            className={`p-3 rounded-xl cursor-pointer transition-all ${
                                                selectedLabelId === label.id 
                                                    ? 'ring-2 bg-white/10' 
                                                    : 'bg-white/5 hover:bg-white/10'
                                            }`}
                                            style={{ ringColor: selectedLabelId === label.id ? primaryColor : 'transparent' }}
                                            onClick={() => setSelectedLabelId(label.id)}
                                        >
                                            <div className="flex items-center gap-3">
                                                <span className="text-xl">{label.icon || '📍'}</span>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-white text-sm font-medium truncate">{label.title}</p>
                                                    {label.subtitle && (
                                                        <p className="text-white/50 text-xs truncate">{label.subtitle}</p>
                                                    )}
                                                </div>
                                                <Button 
                                                    variant="ghost" 
                                                    size="icon" 
                                                    className="h-8 w-8 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded-full"
                                                    onClick={(e) => { e.stopPropagation(); deleteLabel(label.id); }}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Quick Add Button */}
                        {editingLabels.length > 0 && !selectedLabel && (
                            <div className="p-4 border-t border-white/10">
                                <p className="text-white/40 text-xs text-center">
                                    Click on the image to add more labels
                                </p>
                            </div>
                        )}
                    </motion.div>
                )}
            </div>
        </div>
    );
}

// Label Overlay Component
function LabelOverlay({ 
    label, 
    index,
    isSelected, 
    isPreview, 
    primaryColor, 
    onSelect, 
    onCardDragStart,
    onAnchorDragStart,
    onDelete 
}) {
    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, delay: index * 0.1 }}
            className="absolute inset-0 pointer-events-none"
        >
            {/* Connecting Line */}
            <svg className="absolute inset-0 w-full h-full" style={{ overflow: 'visible' }}>
                <motion.line
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
                    x1={`${label.anchor_x_percent}%`}
                    y1={`${label.anchor_y_percent}%`}
                    x2={`${label.card_x_percent}%`}
                    y2={`${label.card_y_percent}%`}
                    stroke="white"
                    strokeWidth={isSelected ? "2" : "1.5"}
                    strokeOpacity={isSelected ? "0.9" : "0.5"}
                    strokeLinecap="round"
                />
            </svg>

            {/* Anchor Pin */}
            <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 500, delay: index * 0.1 }}
                className={`absolute -translate-x-1/2 -translate-y-1/2 pointer-events-auto ${
                    !isPreview ? 'cursor-grab active:cursor-grabbing' : ''
                }`}
                style={{ 
                    left: `${label.anchor_x_percent}%`, 
                    top: `${label.anchor_y_percent}%`,
                }}
                onMouseDown={!isPreview ? onAnchorDragStart : undefined}
                onTouchStart={!isPreview ? onAnchorDragStart : undefined}
                onClick={(e) => { e.stopPropagation(); onSelect(); }}
            >
                <div 
                    className={`w-4 h-4 rounded-full border-2 border-white transition-all ${
                        isSelected ? 'scale-125' : ''
                    }`}
                    style={{ 
                        backgroundColor: primaryColor,
                        boxShadow: `0 0 20px ${primaryColor}80, 0 0 40px ${primaryColor}40`
                    }}
                />
                {/* Pulse effect */}
                <motion.div
                    className="absolute inset-0 rounded-full border-2 border-white/40"
                    initial={{ scale: 1, opacity: 0.6 }}
                    animate={{ scale: 2.5, opacity: 0 }}
                    transition={{ repeat: Infinity, duration: 2, repeatDelay: 1 }}
                />
            </motion.div>

            {/* Label Card */}
            <motion.div
                initial={{ opacity: 0, scale: 0.8, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.15 + index * 0.1 }}
                className={`absolute -translate-x-1/2 -translate-y-1/2 pointer-events-auto ${
                    !isPreview ? 'cursor-grab active:cursor-grabbing' : ''
                }`}
                style={{ 
                    left: `${label.card_x_percent}%`, 
                    top: `${label.card_y_percent}%`,
                }}
                onMouseDown={!isPreview ? onCardDragStart : undefined}
                onTouchStart={!isPreview ? onCardDragStart : undefined}
                onClick={(e) => { e.stopPropagation(); onSelect(); }}
                whileHover={isPreview ? { scale: 1.05 } : {}}
            >
                <div 
                    className={`relative px-3 py-2.5 rounded-xl backdrop-blur-xl border transition-all group ${
                        isSelected && !isPreview ? 'ring-2' : ''
                    }`}
                    style={{ 
                        backgroundColor: 'rgba(45, 55, 30, 0.72)',
                        borderColor: isSelected ? primaryColor : 'rgba(255, 255, 255, 0.25)',
                        ringColor: primaryColor,
                        boxShadow: isSelected 
                            ? `0 15px 50px rgba(0,0,0,0.5), 0 0 30px ${primaryColor}20`
                            : '0 8px 30px rgba(0,0,0,0.4)'
                    }}
                >
                    {/* Glassmorphism shine */}
                    <div 
                        className="absolute inset-0 rounded-xl opacity-20 pointer-events-none"
                        style={{
                            background: 'linear-gradient(160deg, rgba(255,255,255,0.25) 0%, transparent 40%)'
                        }}
                    />
                    
                    <div className="relative">
                        <div className="flex items-center gap-2.5">
                            {label.icon && (
                                <span className="text-lg drop-shadow-sm">{label.icon}</span>
                            )}
                            <p className="text-white text-sm font-bold whitespace-nowrap drop-shadow-sm" style={{ textShadow: '0 1px 3px rgba(0,0,0,0.5)' }}>
                                {label.title}
                            </p>
                        </div>
                        {label.subtitle && (
                            <div className="mt-1 space-y-0.5">
                                {label.subtitle.split(',').map((b, i) => b.trim() && (
                                    <div key={b.trim()} className="flex items-center gap-1.5">
                                        <span className="w-1.5 h-1.5 rounded-sm flex-shrink-0" style={{ backgroundColor: primaryColor }} />
                                        <span className="text-xs" style={{ color: 'rgba(255,255,255,0.85)' }}>{b.trim()}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Edit indicator */}
                    {isSelected && !isPreview && (
                        <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full flex items-center justify-center"
                            style={{ backgroundColor: primaryColor }}
                        >
                            <Move className="w-2.5 h-2.5 text-white" />
                        </motion.div>
                    )}
                </div>
            </motion.div>
        </motion.div>
    );
}

// Label Form Component
function LabelForm({ label, onUpdate, onDelete, onDone, primaryColor }) {
    return (
        <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-5"
        >
            {/* Title */}
            <div>
                <Label className="text-white/70 text-xs uppercase tracking-wider">Ingredient Name</Label>
                <Input 
                    value={label.title}
                    onChange={(e) => onUpdate({ title: e.target.value })}
                    placeholder="e.g., A5 Wagyu"
                    className="mt-2 bg-white/5 border-white/10 text-white text-base focus:border-white/30 rounded-xl"
                    autoFocus
                />
            </div>

            {/* Subtitle */}
            <div>
                <Label className="text-white/70 text-xs uppercase tracking-wider">Benefits (comma-separated)</Label>
                <Input 
                    value={label.subtitle || ''}
                    onChange={(e) => onUpdate({ subtitle: e.target.value })}
                    placeholder="e.g., Premium Grade, Rich Marbling"
                    className="mt-2 bg-white/5 border-white/10 text-white focus:border-white/30 rounded-xl"
                />
            </div>

            {/* Icon Selector */}
            <div>
                <Label className="text-white/70 text-xs uppercase tracking-wider">Icon</Label>
                <div className="flex flex-wrap gap-1.5 mt-2">
                    {EMOJI_OPTIONS.map((emoji) => (
                        <button
                            key={emoji}
                            type="button"
                            onClick={() => onUpdate({ icon: emoji })}
                            className={`w-10 h-10 rounded-xl flex items-center justify-center text-xl transition-all ${
                                label.icon === emoji 
                                    ? 'ring-2 bg-white/20' 
                                    : 'bg-white/5 hover:bg-white/15'
                            }`}
                            style={{ ringColor: label.icon === emoji ? primaryColor : 'transparent' }}
                        >
                            {emoji}
                        </button>
                    ))}
                </div>
            </div>

            {/* Position Info */}
            <div className="pt-4 border-t border-white/10">
                <p className="text-white/40 text-xs flex items-center gap-2">
                    <Move className="w-3 h-3" />
                    Drag the label card on the image to reposition
                </p>
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-2">
                <Button 
                    variant="ghost" 
                    onClick={onDelete}
                    className="flex-1 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-xl"
                >
                    <Trash2 className="w-4 h-4 mr-2" /> Delete
                </Button>
                <Button 
                    onClick={onDone}
                    className="flex-1 rounded-xl"
                    style={{ backgroundColor: primaryColor }}
                >
                    <Check className="w-4 h-4 mr-2" /> Done
                </Button>
            </div>
        </motion.div>
    );
}
